import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminAssets.css';
import { API_BASE } from '../apiConfig';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEdit, faTrash, faSync, faCalculator, 
    faLayerGroup, faPlus, faBars, faCube, faSave, faTimes, faCogs 
} from '@fortawesome/free-solid-svg-icons';

const AdminAssets = () => {
    const { t } = useTranslation();

    // Տվյալների պահպանում
    const [assets, setAssets] = useState([]);
    const [allCalculators, setAllCalculators] = useState([]); 
    const [allNavbars, setAllNavbars] = useState([]);
    const [allGroups, setAllGroups] = useState([]); 
    
    // Խմբագրման վիճակներ (Editing States)
    const [editingType, setEditingType] = useState(null); // 'calc', 'nav', 'group', 'asset'
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    // Ավելացման և ընտրության վիճակներ
    const [selectedCalcId, setSelectedCalcId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeForm, setActiveForm] = useState(null); 
    const [formData, setFormData] = useState({});

    const handleOptionsChange = (currentOptions, key, value) => {
        let opts = {};
        try {
            opts = typeof currentOptions === 'string' ? JSON.parse(currentOptions || '{}') : (currentOptions || {});
        } catch (e) { opts = {}; }
        
        opts[key] = value;
        return JSON.stringify(opts);
    };

    const getAuthHeader = useCallback(() => ({
        headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`, 
            'ngrok-skip-browser-warning': 'true' 
        }
    }), []);

    // Սա տեղադրիր fetchData ֆունկցիայի մեջ կամ անմիջապես հետո
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const header = getAuthHeader();
            const [aRes, cRes, gRes, nRes] = await Promise.all([
                axios.get(`${API_BASE}/api/assets/admin/all`, header),
                axios.get(`${API_BASE}/api/assets/calculators`, header),
                axios.get(`${API_BASE}/api/assets/asset-groups`, header),
                axios.get(`${API_BASE}/api/assets/navbars`, header)
            ]);

            // Կրկնվող հաշվիչները հեռացնելու համար (ըստ անունի)
            const uniqueCalcs = cRes.data.reduce((acc, current) => {
                const x = acc.find(item => item.name === current.name);
                if (!x) return acc.concat([current]);
                else return acc;
            }, []);

            setAssets(aRes.data.data || []);
            setAllCalculators(uniqueCalcs); // Միայն ունիկալները
            setAllGroups(gRes.data || []);
            setAllNavbars(nRes.data || []);
        } catch (err) { 
            console.error("Fetch Error:", err); 
        } finally { 
            setLoading(false); 
        }
    }, [getAuthHeader]);

    // Նոր հաշվիչ ավելացնելու ֆունկցիան
    const handleCreateCalculator = async () => {
        if (!formData.name) return alert(t('admin.name_required'));
        try {
            const header = getAuthHeader();
            await axios.post(`${API_BASE}/api/assets/calculators/add`, { name: formData.name }, header);
            setFormData({});
            setActiveForm(null);
            fetchData(); // Թարմացնել ցուցակը
        } catch (err) {
            alert(t('admin.add_failed'));
        }
    };

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- Խմբագրման ֆունկցիաներ ---
    const startEdit = (type, item) => {
        setEditingType(type);
        setEditingId(item.id);
        setEditData({ ...item });
    };

    const cancelEdit = () => {
        setEditingType(null);
        setEditingId(null);
        setEditData({});
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            const header = getAuthHeader();
            
            // 1. Ստուգում ենք՝ արդյոք ունենք անհրաժեշտ տվյալները
            if (!editingType || !editingId) {
                console.error("Missing editingType or editingId", { editingType, editingId });
                alert(t('admin.missing_edit_data'));
                return;
            }

            let endpoint = '';
            if (editingType === 'calc') endpoint = `/api/assets/calculators/update/${editingId}`;
            else if (editingType === 'nav') endpoint = `/api/assets/navbars/update/${editingId}`;
            else if (editingType === 'group') endpoint = `/api/assets/asset-groups/update/${editingId}`;
            else if (editingType === 'asset') endpoint = `/api/assets/admin/update/${editingId}`;

            // 2. Եթե վերևի պայմաններից ոչ մեկը չաշխատեց
            if (!endpoint) {
                alert(t('admin.endpoint_not_found'));
                return;
            }

            console.log("Sending PUT to:", `${API_BASE}${endpoint}`);
            console.log("Data:", editData);

            const response = await axios.put(`${API_BASE}${endpoint}`, editData, header);

            if (response.data.success) {
                alert(t('common.updated_successfully'));
                cancelEdit();
                fetchData();
            }
        } catch (err) {
            console.error("Update Error details:", err.response?.data || err.message);
            alert(err.response?.data?.message || t('common.update_error'));
        } finally {
            setLoading(false);
        }
    };

    // --- Ավելացման և Ջնջման ֆունկցիաներ ---
    const handleSaveNew = async (type) => {
        try {
            const header = getAuthHeader();
            let endpoint = '';
            let payload = { ...formData };
            
            // 1. Եթե ավելացնում ենք հենց ՀԱՇՎԻՉ (Main Calculator)
            if (type === 'calc_main') {
                endpoint = '/api/assets/calculators/add';
                // Հաշվիչի համար սովորաբար ID-ն ավտոմատ է գնում, եթե ոչ՝ կարող ես ավելացնել
            } 
            else {
                // Մնացած բոլորի համար (nav, group, asset) պետք է ընտրված հաշվիչի ID-ն
                const calcId = parseInt(selectedCalcId);
                if (!calcId) return alert(t('admin.select_calculator_first'));
                
                payload.calculator_id = calcId;
                let minId, maxId, currentItems;

                if (type === 'nav') {
                    endpoint = '/api/assets/navbars/add';
                    minId = calcId * 10;
                    maxId = minId + 9;
                    currentItems = allNavbars.filter(n => parseInt(n.id) >= minId && parseInt(n.id) <= maxId);
                    payload.id = currentItems.length > 0 ? Math.max(...currentItems.map(n => parseInt(n.id))) + 1 : minId;
                } 
                else if (type === 'group') {
                    endpoint = '/api/assets/asset-groups/add';
                    minId = calcId * 100;
                    maxId = minId + 99;
                    currentItems = allGroups.filter(g => parseInt(g.id) >= minId && parseInt(g.id) <= maxId);
                    payload.id = currentItems.length > 0 ? Math.max(...currentItems.map(g => parseInt(g.id))) + 1 : minId;
                    payload.group_name = formData.name;
                } 
                else if (type === 'asset') {
                    endpoint = '/api/assets/admin/add';
                    minId = calcId * 1000;
                    maxId = minId + 999;
                    currentItems = assets.filter(a => parseInt(a.id) >= minId && parseInt(a.id) <= maxId);
                    payload.id = currentItems.length > 0 ? Math.max(...currentItems.map(a => parseInt(a.id))) + 1 : minId;
                    
                    payload.std_factor = formData.price || 0;
                    payload.price = formData.price || 0;
                    payload.unit = formData.unit || t('admin.unit_default');
                    payload.type = 'simple';
                    payload.unit_weight = formData.unit_weight || 0;
                    payload.unit_volume = formData.unit_volume || 0;
                }

                if (payload.id > maxId) return alert(`${t('admin.limit_reached')} (${maxId})`);
            }

            await axios.post(`${API_BASE}${endpoint}`, payload, header);
            
            setActiveForm(null);
            setFormData({});
            fetchData();
            alert(t('common.saved_successfully'));
        } catch (err) {
            console.error("Save Error:", err);
            alert(err.response?.data?.message || t('common.save_error'));
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm(t('common.are_you_sure'))) return;
        try {
            const header = getAuthHeader();
            let url = '';
            if (type === 'nav') url = `/api/assets/navbars/${id}`;
            if (type === 'group') url = `/api/assets/asset-groups/${id}`;
            if (type === 'asset') url = `/api/assets/admin/delete/${id}`;
            if (type === 'calc') url = `/api/assets/calculators/${id}`;

            await axios.delete(`${API_BASE}${url}`, header);
            fetchData();
        } catch (err) { 
            alert(t('common.delete_error')); 
        }
    };
return (
    <div style={{ color: '#1B2559', padding: '30px', backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: 'sans-serif' }}>
        
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
            <h2 style={{margin: 0}}>{t('admin.calculator_management')}</h2>
            <button onClick={fetchData} style={refreshBtn}><FontAwesomeIcon icon={faSync} spin={loading} /></button>
        </div>

        {/* --- 1. CALCULATORS GRID --- */}
        <div style={calcGrid}>
    {allCalculators.map(calc => (
        <div 
            key={calc.id} 
            onClick={() => setSelectedCalcId(calc.id)} 
            style={selectedCalcId === calc.id ? activeCalcCard : calcCard}
        >
            {editingId === calc.id && editingType === 'calc' ? (
                /* Խմբագրման ռեժիմ */
                <div onClick={e => e.stopPropagation()} style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                    <input 
                        style={smallInput} 
                        value={editData.name} 
                        onChange={e => setEditData({...editData, name: e.target.value})} 
                    />
                    <div style={{display:'flex', gap:'10px', justifyContent:'center'}}>
                        <FontAwesomeIcon icon={faSave} onClick={handleUpdate} style={saveIco} />
                        <FontAwesomeIcon icon={faTimes} onClick={cancelEdit} style={deleteIco} />
                    </div>
                </div>
            ) : (
                /* Սովորական դիտման ռեժիմ */
                <>
                    <div style={iconCircle}>
                        <FontAwesomeIcon icon={faCalculator} />
                    </div>
                    <span style={calcName}>{calc.name}</span>
                    <div style={cardActions}>
                        <FontAwesomeIcon 
                            icon={faEdit} 
                            style={editIco} 
                            onClick={(e) => {e.stopPropagation(); startEdit('calc', calc)}} 
                        />
                        <FontAwesomeIcon 
                            icon={faTrash} 
                            style={deleteIco} 
                            onClick={(e) => {e.stopPropagation(); handleDelete('calc', calc.id)}} 
                        />
                    </div>
                </>
            )}
        </div>
    ))}

    {/* ՆՈՐ ՀԱՇՎԻՉ ԱՎԵԼԱՑՆԵԼՈՒ ՀԱՏՎԱԾԸ */}
    {activeForm === 'calc_main' ? (
        <div style={addCalcCardActive}>
            <input 
                autoFocus
                style={smallInput} 
                placeholder={`${t('admin.name')}...`} 
                value={formData.name || ''}
                onChange={e => setFormData({...formData, name: e.target.value})}
            />
            <div style={{display:'flex', gap:'10px', marginTop:'10px'}}>
                <button onClick={handleCreateCalculator} style={miniSaveBtn}>{t('common.add')}</button>
                <button onClick={() => {setActiveForm(null); setFormData({});}} style={miniCancelBtn}>X</button>
            </div>
        </div>
    ) : (
        <div style={addCalcCard} onClick={() => setActiveForm('calc_main')}>
            <div style={{...iconCircle, backgroundColor: '#E0E5F2', color: '#A3AED0'}}>
                <FontAwesomeIcon icon={faPlus} />
            </div>
            <span style={{fontWeight: '600', color: '#A3AED0'}}>{t('admin.new_calculator')}</span>
        </div>
    )}
</div>

        {selectedCalcId && (
            <div style={detailsArea}>
                
                {/* --- 2. NAVBARS SECTION --- */}
                <div style={sectionCard}>
                    <h4 style={sectionHeader}><FontAwesomeIcon icon={faBars} /> {t('admin.navbars')}</h4>
                    {allNavbars.filter(n => n.calculator_id === selectedCalcId).map(nav => (
                        <div key={nav.id} style={itemRow}>
                            {editingId === nav.id && editingType === 'nav' ? (
                                <div style={{display:'flex', gap:'10px', width:'100%'}}>
                                    <input style={smallInput} value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} />
                                    <FontAwesomeIcon icon={faSave} style={saveIco} onClick={handleUpdate} />
                                    <FontAwesomeIcon icon={faTimes} style={deleteIco} onClick={cancelEdit} />
                                </div>
                            ) : (
                                <>
                                    <span>{nav.name}</span>
                                    <div style={{display:'flex', gap:'10px'}}>
                                        <FontAwesomeIcon icon={faEdit} style={editIco} onClick={() => startEdit('nav', nav)} />
                                        <FontAwesomeIcon icon={faTrash} style={deleteIco} onClick={() => handleDelete('nav', nav.id)} />
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                    {activeForm === 'nav' ? (
                        <div style={inlineForm}>
                            <input placeholder={t('admin.name')} style={smallInput} onChange={e => setFormData({...formData, name: e.target.value})} />
                            <button onClick={() => handleSaveNew('nav')} style={saveBtn}>OK</button>
                        </div>
                    ) : <button style={addListBtn} onClick={() => setActiveForm('nav')}>+ {t('admin.add_navbar')}</button>}
                </div>

                {/* --- 3. GROUPS SECTION --- */}
                <div style={sectionCard}>
                    <h4 style={sectionHeader}><FontAwesomeIcon icon={faLayerGroup} /> {t('admin.groups')}</h4>
                    {allGroups.filter(g => g.calculator_id === selectedCalcId).map(group => (
                        <div key={group.id} style={itemRow}>
                            {editingId === group.id && editingType === 'group' ? (
                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:'5px', width:'100%'}}>
                                    <select style={smallInput} value={editData.navbar_id} onChange={e => setEditData({...editData, navbar_id: e.target.value})}>
                                        {allNavbars.filter(n => n.calculator_id === selectedCalcId).map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                                    </select>
                                    <input style={smallInput} value={editData.group_name} onChange={e => setEditData({...editData, group_name: e.target.value})} />
                                    <FontAwesomeIcon icon={faSave} style={saveIco} onClick={handleUpdate} />
                                    <FontAwesomeIcon icon={faTimes} style={deleteIco} onClick={cancelEdit} />
                                </div>
                            ) : (
                                <>
                                    <span>{group.group_name} <small style={{color:'#A3AED0'}}>({allNavbars.find(n => n.id === group.navbar_id)?.name})</small></span>
                                    <div style={{display:'flex', gap:'10px'}}>
                                        <FontAwesomeIcon icon={faEdit} style={editIco} onClick={() => startEdit('group', group)} />
                                        <FontAwesomeIcon icon={faTrash} style={deleteIco} onClick={() => handleDelete('group', group.id)} />
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                    {activeForm === 'group' ? (
                        <div style={inlineForm}>
                            <select style={smallInput} onChange={e => setFormData({...formData, navbar_id: e.target.value})}>
                                <option>Navbar</option>
                                {allNavbars.filter(n => n.calculator_id === selectedCalcId).map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                            </select>
                            <input placeholder={t('admin.group_name')} style={smallInput} onChange={e => setFormData({...formData, name: e.target.value})} />
                            <button onClick={() => handleSaveNew('group')} style={saveBtn}>OK</button>
                        </div>
                    ) : <button style={addListBtn} onClick={() => setActiveForm('group')}>+ {t('admin.add_group')}</button>}
                </div>

                {/* --- 4. ASSETS TABLE SECTION --- */}
                <div style={sectionCard}>
                    <h4 style={sectionHeader}><FontAwesomeIcon icon={faCube} /> {t('admin.asset_list')}</h4>
                    <table style={tableStyle}>
<thead>
    <tr style={thRow}>
        <th>{t('admin.icon')}</th>
        <th>{t('admin.name')}</th>
        
        {/* 1. WASTE (Շինաղբ) */}
        {selectedCalcId === 1 && (
            <>
                <th>{t('admin.factor')}</th>
                <th>{t('admin.weight')}</th>
                <th>{t('admin.volume')}</th>
            </>
        )}

        {/* 3. MOVING (Բնակարանների տեղափոխում) */}
        {selectedCalcId === 3 && (
            <>
                <th>{t('admin.volume_m3')}</th>
                <th>{t('admin.elevator')}</th>
            </>
        )}

        {/* 2. DEMOLITION (Քանդում) */}
        {selectedCalcId === 2 && (
            <>
                <th>{t('admin.unit')}</th>
                <th>{t('admin.front_id')}</th>
                <th>{t('admin.density')}</th>
            </>
        )}

        {/* 5. MASTER */}
        {selectedCalcId === 5 && <th>{t('admin.type')}</th>}

        {/* 6. MACHINERY */}
        {selectedCalcId === 6 && (
            <>
                <th>{t('admin.min_hours')}</th>
                <th>{t('admin.extra_price_hr')}</th>
            </>
        )}

        {/* 7. LOGISTICS */}
        {selectedCalcId === 7 && <th>{t('admin.key')}</th>}

        <th>{t('admin.group')}</th>
        <th>{t('admin.price')}</th>
        <th>{t('admin.actions')}</th>
    </tr>
</thead>
 
 
  <tbody>
    {/* 1. ԳՈՅՈՒԹՅՈՒՆ ՈՒՆԵՑՈՂ ԻՐԵՐԻ ՑՈՒՑԱԿ */}
    {assets.filter(a => a.calculator_id === selectedCalcId).map(asset => (
        <tr key={asset.id}>
            {editingId === asset.id && editingType === 'asset' ? (
                <>
                    {/* EDIT MODE */}
                    <td><input style={tableInput} value={editData.icon} onChange={e => setEditData({...editData, icon: e.target.value})} /></td>
                    <td><input style={tableInput} value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} /></td>
                    
                    {/* Դինամիկ դաշտեր ըստ Calc ID-ների */}
                    {selectedCalcId === 3 ? (
                        <>
                            <td>
                                <input 
                                    style={tableInput} 
                                    type="number" 
                                    step="0.01"
                                    value={editData.unit_volume || ''} 
                                    onChange={e => setEditData({...editData, unit_volume: e.target.value})} 
                                />
                            </td>
                            <td>
                                <select 
                                    style={tableInput} 
                                    value={JSON.parse(editData.options || '{}').fitsInElevator ?? true} 
                                    onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'fitsInElevator', e.target.value === 'true')})}
                                >
                                    <option value="true">{t('admin.fits_elevator')}</option>
                                    <option value="false">{t('admin.no_elevator')}</option>
                                </select>
                            </td>
                        </>
                    ) : selectedCalcId === 6 ? (
                        <>
                            <td><input style={tableInput} type="number" value={JSON.parse(editData.options || '{}').min_hours || ''} onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'min_hours', e.target.value)})} /></td>
                            <td><input style={tableInput} type="number" value={JSON.parse(editData.options || '{}').extra_hour_price || ''} onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'extra_hour_price', e.target.value)})} /></td>
                        </>
                    ) : selectedCalcId === 7 ? (
                        <td>
                            <select style={tableInput} value={JSON.parse(editData.options || '{}').key || 'ton'} onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'key', e.target.value)})}>
                                <option value="ton">{t('admin.ton')}</option>
                                <option value="tech">{t('admin.tech')}</option>
                                <option value="wood">{t('admin.wood')}</option>
                                <option value="pallet">{t('admin.pallet')}</option>
                                <option value="big_box">{t('admin.big_box')}</option>
                            </select>
                        </td>
                    ) : selectedCalcId === 2 ? (
                        <>
                            <td>
                                <select style={tableInput} value={editData.unit} onChange={e => setEditData({...editData, unit: e.target.value})}>
                                    <option value="m2">{t('admin.unit_m2')}</option>
                                    <option value="gmetr">{t('admin.unit_gmetr')}</option>
                                    <option value="hat">{t('admin.unit_hat')}</option>
                                </select>
                            </td>
                            <td><input style={tableInput} value={JSON.parse(editData.options || '{}').frontend_id || ''} onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'frontend_id', e.target.value)})} /></td>
                            <td><input style={tableInput} type="number" value={JSON.parse(editData.options || '{}').density || ''} onChange={e => setEditData({...editData, options: handleOptionsChange(editData.options, 'density', e.target.value)})} /></td>
                        </>
                    ) : (
                        <>
                            {selectedCalcId === 5 && <td>
                                <select style={tableInput} value={editData.type} onChange={e => setEditData({...editData, type: e.target.value})}>
                                    <option value="fixed">Fixed</option>
                                    <option value="meter">Meter</option>
                                </select>
                            </td>}
                            {selectedCalcId === 1 && <>
                                <td><input style={tableInput} value={editData.std_factor} onChange={e => setEditData({...editData, std_factor: e.target.value})} /></td>
                                <td><input style={tableInput} value={editData.unit_weight} onChange={e => setEditData({...editData, unit_weight: e.target.value})} /></td>
                                <td><input style={tableInput} value={editData.unit_volume} onChange={e => setEditData({...editData, unit_volume: e.target.value})} /></td>
                            </>}
                        </>
                    )}

                    <td>
                        <select style={tableInput} value={editData.group_id} onChange={e => setEditData({...editData, group_id: e.target.value})}>
                            {allGroups.filter(g => g.calculator_id === selectedCalcId).map(g => <option key={g.id} value={g.id}>{g.group_name}</option>)}
                        </select>
                    </td>
                    <td><input style={tableInput} value={editData.price} onChange={e => setEditData({...editData, price: e.target.value})} /></td>
                    <td style={{display:'flex', gap:'10px', padding:'12px 0'}}>
                        <FontAwesomeIcon icon={faSave} style={saveIco} onClick={handleUpdate} />
                        <FontAwesomeIcon icon={faTimes} style={deleteIco} onClick={cancelEdit} />
                    </td>
                </>
            ) : (
                <>
                    {/* VIEW MODE */}
                    <td style={tdStyle}>{asset.icon}</td>
                    <td style={tdStyle}><b>{asset.name}</b></td>
                    
                    {selectedCalcId === 3 ? (
                        <>
                            <td style={tdStyle}>{asset.unit_volume} {t('admin.unit_m3')}</td>
                            <td style={tdStyle}>
                                <span style={badgeStyle}>
                                    {JSON.parse(asset.options || '{}').fitsInElevator ? t('admin.fits_elevator') : t('admin.no_elevator')}
                                </span>
                            </td>
                        </>
                    ) : selectedCalcId === 6 ? (
                        <>
                            <td style={tdStyle}>{JSON.parse(asset.options || '{}').min_hours || 0}{t('admin.h')}</td>
                            <td style={tdStyle}>{JSON.parse(asset.options || '{}').extra_hour_price || 0}֏</td>
                        </>
                    ) : selectedCalcId === 7 ? (
                        <td style={tdStyle}><span style={badgeStyle}>{JSON.parse(asset.options || '{}').key || 'ton'}</span></td>
                    ) : selectedCalcId === 2 ? (
                        <>
                            <td style={tdStyle}><span style={badgeStyle}>{asset.unit || 'm2'}</span></td>
                            <td style={tdStyle}>{JSON.parse(asset.options || '{}').frontend_id || '-'}</td>
                            <td style={tdStyle}>{JSON.parse(asset.options || '{}').density || 0}</td>
                        </>
                    ) : (
                        <>
                            {selectedCalcId === 5 && <td style={tdStyle}><span style={badgeStyle}>{asset.type || 'fixed'}</span></td>}
                            {selectedCalcId === 1 && <>
                                <td style={tdStyle}>{asset.std_factor || 1.0}</td>
                                <td style={tdStyle}>{asset.unit_weight}</td>
                                <td style={tdStyle}>{asset.unit_volume}</td>
                            </>}
                        </>
                    )}

                    <td style={tdStyle}>{allGroups.find(g => g.id === asset.group_id)?.group_name}</td>
                    <td style={tdStyle}>{asset.price} ֏</td>
                    <td style={tdStyle}>
                        <FontAwesomeIcon icon={faEdit} style={{...editIco, marginRight: '10px'}} onClick={() => startEdit('asset', asset)} />
                        <FontAwesomeIcon icon={faTrash} style={deleteIco} onClick={() => handleDelete('asset', asset.id)} />
                    </td>
                </>
            )}
        </tr>
    ))}

    {/* 2. ԱՎԵԼԱՑՄԱՆ ՏՈՂ */}
    {activeForm === 'asset' && (
        <tr style={{ background: '#f0f4ff' }}>
            <td><input placeholder={t('admin.icon')} style={tableInput} onChange={e => setFormData({...formData, icon: e.target.value})} /></td>
            <td><input placeholder={t('admin.name')} style={tableInput} onChange={e => setFormData({...formData, name: e.target.value})} /></td>
            
            {selectedCalcId === 3 ? (
                <>
                    <td><input placeholder={t('admin.volume')} type="number" step="0.01" style={tableInput} onChange={e => setFormData({...formData, unit_volume: e.target.value})} /></td>
                    <td>
                        <select style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'fitsInElevator', e.target.value === 'true')})}>
                            <option value="true">{t('admin.fits_elevator')}</option>
                            <option value="false">{t('admin.no_elevator')}</option>
                        </select>
                    </td>
                </>
            ) : selectedCalcId === 6 ? (
                <>
                    <td><input placeholder={t('admin.min_hours')} type="number" style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'min_hours', e.target.value)})} /></td>
                    <td><input placeholder={t('admin.extra_price_hr')} type="number" style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'extra_hour_price', e.target.value)})} /></td>
                </>
            ) : selectedCalcId === 7 ? (
                <td>
                    <select style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'key', e.target.value)})}>
                        <option value="ton">{t('admin.ton')}</option>
                        <option value="tech">{t('admin.tech')}</option>
                        <option value="wood">{t('admin.wood')}</option>
                        <option value="pallet">{t('admin.pallet')}</option>
                        <option value="big_box">{t('admin.big_box')}</option>
                    </select>
                </td>
            ) : selectedCalcId === 2 ? (
                <>
                    <td>
                        <select style={tableInput} onChange={e => setFormData({...formData, unit: e.target.value})}>
                            <option value="m2">{t('admin.unit_m2')}</option>
                            <option value="gmetr">{t('admin.unit_gmetr')}</option>
                            <option value="hat">{t('admin.unit_hat')}</option>
                        </select>
                    </td>
                    <td><input placeholder="frontend_id" style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'frontend_id', e.target.value)})} /></td>
                    <td><input placeholder={t('admin.density')} type="number" style={tableInput} onChange={e => setFormData({...formData, options: handleOptionsChange(formData.options, 'density', e.target.value)})} /></td>
                </>
            ) : (
                <>
                    {selectedCalcId === 5 && <td>
                        <select style={tableInput} onChange={e => setFormData({...formData, type: e.target.value})}>
                            <option value="fixed">Fixed</option>
                            <option value="meter">Meter</option>
                        </select>
                    </td>}
                    {selectedCalcId === 1 && <>
                        <td><input placeholder={t('admin.factor')} style={tableInput} onChange={e => setFormData({...formData, std_factor: e.target.value})} /></td>
                        <td><input placeholder={t('admin.weight')} style={tableInput} onChange={e => setFormData({...formData, unit_weight: e.target.value})} /></td>
                        <td><input placeholder={t('admin.volume')} style={tableInput} onChange={e => setFormData({...formData, unit_volume: e.target.value})} /></td>
                    </>}
                </>
            )}

            <td>
                <select style={tableInput} onChange={e => setFormData({...formData, group_id: e.target.value})}>
                    <option value="">{t('admin.group')}</option>
                    {allGroups.filter(g => g.calculator_id === selectedCalcId).map(g => (
                        <option key={g.id} value={g.id}>{g.group_name}</option>
                    ))}
                </select>
            </td>
            <td><input placeholder={t('admin.price')} style={tableInput} onChange={e => setFormData({...formData, price: e.target.value})} /></td>
            <td><button onClick={() => handleSaveNew('asset')} style={saveBtn}>OK</button></td>
        </tr>
    )}

    {!activeForm && (
        <tr>
            <td colSpan="12">
                <button style={addListBtn} onClick={() => setActiveForm('asset')}>+ {t('admin.add_new_asset')}</button>
            </td>
        </tr>
    )}
</tbody>
                    </table>
                </div>

{/* --- 5. CALCULATOR SETTINGS --- */}
<div style={{...sectionCard, border: '1px solid #4318FF'}}>
    <h4 style={{...sectionHeader, color: '#4318FF'}}>
        <FontAwesomeIcon icon={faCogs} /> {t('admin.calculator_settings')} ({allCalculators.find(c => c.id === selectedCalcId)?.name})
    </h4>
    
    {allCalculators.filter(c => c.id === selectedCalcId).map(calc => (
        <div key={calc.id}>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px'}}>
                
                {/* 1. Min Order Price */}
                <div>
                    <label style={lab}>{t('admin.min_order_price')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.min_order_price : calc.min_order_price || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, min_order_price: e.target.value}); }} />
                </div>

                {/* 2. Base Weight */}
                <div>
                    <label style={lab}>{t('admin.base_weight')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.base_weight : calc.base_weight || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, base_weight: e.target.value}); }} />
                </div>

                {/* 3. Base Volume */}
                <div>
                    <label style={lab}>{t('admin.base_volume')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.base_volume : calc.base_volume || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, base_volume: e.target.value}); }} />
                </div>

                {/* 4. Price per KM */}
                <div>
                    <label style={lab}>{t('admin.price_per_km')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.price_per_km : calc.price_per_km || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, price_per_km: e.target.value}); }} />
                </div>

                {/* 5. Free Distance */}
                <div>
                    <label style={lab}>{t('admin.free_distance')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.free_distance : calc.free_distance || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, free_distance: e.target.value}); }} />
                </div>

                {/* 6. Elevator Fee */}
                <div>
                    <label style={lab}>{t('admin.elevator_fee')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.elevator_fee_per_unit : calc.elevator_fee_per_unit || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, elevator_fee_per_unit: e.target.value}); }} />
                </div>

                {/* 7. Stairs Fee */}
                <div>
                    <label style={lab}>{t('admin.stairs_fee')}</label>
                    <input type="number" style={smallInput} 
                        value={editData.id === calc.id ? editData.stairs_fee_per_unit : calc.stairs_fee_per_unit || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, stairs_fee_per_unit: e.target.value}); }} />
                </div>

                {/* 8. Discounts (JSON) */}
                <div>
                    <label style={lab}>{t('admin.discounts_json')}</label>
                    <input style={smallInput} 
                        value={editData.id === calc.id ? editData.discount_config : calc.discount_config || ''} 
                        onChange={e => { setEditingType('calc'); setEditingId(calc.id); setEditData({...calc, ...editData, discount_config: e.target.value}); }} />
                </div>

            </div>
            
            <button onClick={handleUpdate} style={{...saveBtn, marginTop: '15px', width: '100%', opacity: editingType === 'calc' ? 1 : 0.7}}>
                {loading ? t('common.saving') : t('common.save_changes')}
            </button>
        </div>
    ))}
</div>
            </div>
        )}
    </div>
);}

// --- ALL STYLES (COMPLETE VERSION) ---
const containerStyle = { padding: '40px', backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: 'Arial' };
const headerNav = { display: 'flex', justifyContent: 'space-between', marginBottom: '30px' };
const badgeStyle = { background: '#E2E8F0', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', color: '#475569', fontWeight: 'bold', display: 'inline-block' };

const calcGrid = { display: 'flex', gap: '15px', marginBottom: '25px', overflowX: 'auto', paddingBottom: '10px' };
// 1. Հիմնական աշխատանքային գոտին
const detailsArea = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    marginTop: '30px'
};

// 2. Սա նոր օբյեկտ է, որը պետք է ավելացնես return-ի մեջ Navbar-ի ու Group-ի համար
const flexRow = {
    display: 'flex',
    flexDirection: 'row',
    gap: '25px',
    width: '100%'
};

// 3. Քարտի սթայլը (ավելացրու flex: 1)
const sectionCard = {
    flex: 1, // Սա թույլ կտա Navbar-ին ու Group-ին կիսել տեղը
    background: '#ffffff',
    borderRadius: '24px',
    padding: '25px',
    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.04)',
    border: '1px solid #F4F7FE',
    minWidth: 0 // Կանխում է սեղմվելը
};

// 4. Աղյուսակի սթայլը (Assets-ի համար)
const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '15px'
};
const calcCard = { 
    minWidth: '160px', background: '#fff', padding: '20px', borderRadius: '20px', 
    display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', 
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', transition: '0.3s' 
};

const activeCalcCard = { ...calcCard, background: '#4318FF', color: '#fff' };
const addCalcCard = { ...calcCard, border: '2px dashed #A3AED0', background: 'transparent', color: '#A3AED0' };
const addCalcCardActive = { ...calcCard, border: '2px solid #4318FF' };

const iconCircle = {
    width: '40px', height: '40px', borderRadius: '50%',
    backgroundColor: 'rgba(163, 174, 208, 0.1)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px'
};

const calcName = { fontWeight: 'bold', fontSize: '14px', textAlign: 'center' };
const cardActions = { position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px', fontSize: '12px' };

const sectionHeader = { marginBottom: '15px', borderBottom: '1px solid #F4F7FE', paddingBottom: '10px', color: '#2B3674', display: 'flex', alignItems: 'center', gap: '10px' };

const itemRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F4F7FE', fontSize: '14px' };

const editIco = { cursor: 'pointer', color: '#4318FF' };
const saveIco = { color: '#22C55E', cursor: 'pointer' };
const deleteIco = { color: '#EE5D50', cursor: 'pointer' };

const inlineForm = { display: 'flex', gap: '8px', padding: '10px 0', alignItems: 'center' };
const smallInput = { padding: '8px 12px', borderRadius: '10px', border: '1px solid #E0E5F2', width: '100%', outline: 'none', fontSize: '13px' };
const tableInput = { padding: '6px 10px', borderRadius: '8px', border: '1px solid #E0E5F2', width: '90%', outline: 'none', fontSize: '12px' };

const lab = { fontSize: '11px', color: '#A3AED0', fontWeight: 'bold', marginBottom: '5px', display: 'block', textTransform: 'uppercase' };
const saveBtn = { background: '#22C55E', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' };

const miniSaveBtn = { background: '#22C55E', color: '#fff', border: 'none', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer' };
const miniCancelBtn = { background: '#EE5D50', color: '#fff', border: 'none', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer' };

const addListBtn = { background: 'none', border: 'none', color: '#4318FF', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', padding: '10px 0', width: '100%', textAlign: 'left', marginTop: '10px' };
const thRow = { textAlign: 'left', fontSize: '12px', color: '#A3AED0', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '12px 0', borderBottom: '1px solid #F4F7FE' };
const refreshBtn = { background: '#fff', border: 'none', padding: '12px', borderRadius: '12px', cursor: 'pointer', color: '#4318FF', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' };

export default AdminAssets;